import { z } from 'zod'

import { CoverDataSchema } from '~/@entities/DesignTemplate'
import { PdfTypeSchema, PrintTypeSchema } from '~/@entities/PodProduct'
import {
  AllGradeSubjectSchema,
  RevisionSchema,
  SchoolTypeSchema,
  SemesterSchema,
} from '~/@entities/SchoolSystem'
import { SignatureTypeSchema } from '~/@entities/Workbook'

/***************** 결제 주체 *****************/
// 선생님이 직접 결제하거나, 학생별 학부모에게 결제 요청을 보낸다
export const PAYMENT_PAYER_TYPE = ['DIRECT_PAYMENT', 'PARENT_PAYMENT'] as const

export const PaymentPayerTypeSchema = z.enum(PAYMENT_PAYER_TYPE)
export type PaymentPayerType = z.infer<typeof PaymentPayerTypeSchema>

/***************** 분권 *****************/
// N제처럼 여러 권으로 나눠 인쇄하는 교재의 권별 제목·책등 제목·쪽수
export const WorkbookPartSchema = z.object({
  title: z.string(),
  sideTitle: z.string(),
  pageCount: z.number(),
})
export type WorkbookPart = z.infer<typeof WorkbookPartSchema>

/***************** 학부모 결제 대상 *****************/
export const ParentOrderTargetSchema = z.object({
  studentId: z.string(),
  studentName: z.string(),
  parentPhoneNumber: z.string(),
  printingUserName: z.string(), // gitleaks:allow
})
export type ParentOrderTarget = z.infer<typeof ParentOrderTargetSchema>

/***************** 장바구니 항목 *****************/
// 서버는 상품 종류마다 다른 클래스를 판별 필드 없이 내보내고 productType 이 그 종류다.
// fulltitle 은 서버가 name 과 같은 값으로 싣는 삭제 예정 필드라 받지 않는다.
const _CartItemCommonSchema = z.object({
  id: z.number(),
  name: z.string(),
  productId: z.number(),
  paymentPayerType: PaymentPayerTypeSchema,
  thumbnailImageUrl: z.string(),
  // 인쇄할 학생 수. 서버가 studentNames 길이로 계산해 싣는다
  minQuantity: z.number(),
  quantity: z.number(),
  price: z.number(),
  discountedPrice: z.number(),
  additionalPrice: z.number(),
  productDiscountRate: z.number(),
  productAmount: z.number(),
  discountAmount: z.number(),
  studentNames: z.array(z.string()),
  printType: PrintTypeSchema,
  pageCount: z.number(),
})

export const SignatureWorkbookCartItemSchema = _CartItemCommonSchema.extend({
  productType: z.literal('SIGNATURE_WORKBOOK'),
  pdfType: PdfTypeSchema,
  coverData: CoverDataSchema,
  workbookParts: z.array(WorkbookPartSchema).nullable(),
  parentOrders: z.array(ParentOrderTargetSchema).nullable(),
  showPrice: z.boolean(),
  problemPageCount: z.number(),
  problemAnswerPageCount: z.number(),
  revision: RevisionSchema,
  schoolType: SchoolTypeSchema,
  grade: AllGradeSubjectSchema,
  semester: SemesterSchema,
  pairFlag: z.boolean(),
  autoScored: z.boolean(),
  signatureType: SignatureTypeSchema,
})
export type SignatureWorkbookCartItem = z.infer<typeof SignatureWorkbookCartItemSchema>

export const CustomWorkbookCartItemSchema = _CartItemCommonSchema.extend({
  productType: z.literal('CUSTOM_WORKBOOK'),
  customWorkbookId: z.number(),
  pdfType: PdfTypeSchema,
  coverType: z.number(),
  coverData: CoverDataSchema.nullable(),
  parentOrders: z.array(ParentOrderTargetSchema).nullable(),
  showPrice: z.boolean(),
  problemPageCount: z.number(),
  problemAnswerPageCount: z.number(),
  innerPdfUrl: z.string(),
  pairFlag: z.boolean(),
  autoScored: z.boolean(),
  // 담은 뒤 교재 내용이 바뀌어 가격이 달라졌는지
  isChangedPrice: z.boolean(),
})
export type CustomWorkbookCartItem = z.infer<typeof CustomWorkbookCartItemSchema>

export const PrintDocumentCartItemSchema = _CartItemCommonSchema.extend({
  productType: z.literal('PRINT_DOCUMENT'),
  printDocumentId: z.number(),
  coverData: CoverDataSchema,
})
export type PrintDocumentCartItem = z.infer<typeof PrintDocumentCartItemSchema>

export const CartItemSchema = z.discriminatedUnion('productType', [
  SignatureWorkbookCartItemSchema,
  CustomWorkbookCartItemSchema,
  PrintDocumentCartItemSchema,
])
export type CartItem = z.infer<typeof CartItemSchema>

/***************** 장바구니 *****************/
// 배송비는 담긴 항목 전체에 한 번 붙는다
export const CartSchema = z.object({
  cartItems: z.array(CartItemSchema),
  deliveryAmount: z.number(),
})
export type Cart = z.infer<typeof CartSchema>

/***************** 장바구니 항목 수정 결과 *****************/
export const CartItemPriceSchema = z.object({
  quantity: z.number(),
  price: z.number(),
  discountedPrice: z.number(),
  additionalPrice: z.number(),
  productDiscountRate: z.number(),
  productAmount: z.number(),
  discountAmount: z.number(),
  totalAmount: z.number(),
})
export type CartItemPrice = z.infer<typeof CartItemPriceSchema>

/***************** PDF 내려받기 주소 *****************/
// 장바구니·주문 항목의 미리보기 PDF. 서명된 주소라 짧은 시간만 유효하다
export const PdfDownloadSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type PdfDownload = z.infer<typeof PdfDownloadSchema>
